import { z } from 'zod';

/*
 * The Atlas v2 document (`merge_lint_json`): `business_profile`,
 * `proposed_solutions` and `departments`, as Canvas renders it. Final review
 * edits it under Atlas's rules (`atlas/merge_lint_edit.py`): prose may change
 * anywhere, whole departments may be removed (except one the executive
 * diagnosis names), and nothing else may change. Removing a department re-runs
 * the merge derivation (`atlas/contract/merge_derived.py` `merge`), ported here
 * line for line, so the panel sends exactly what Atlas expects.
 */

type Obj = Record<string, unknown>;

/** The stored document. Loose: only what the panel relies on is checked. */
export const AtlasDocumentSchema = z.looseObject({
  atlas_version: z.literal('2'),
  business_profile: z.looseObject({
    executive_diagnosis: z.record(z.string(), z.unknown()),
  }),
  proposed_solutions: z.looseObject({ solutions: z.array(z.looseObject({ id: z.string() })) }),
  departments: z.array(z.looseObject({ id: z.string(), name: z.string() })),
});
export type AtlasDocument = z.infer<typeof AtlasDocumentSchema>;

/** The `merge_lint` stage download: the document plus what Atlas found while merging. */
export const MergeLintStageSchema = z.looseObject({
  document: AtlasDocumentSchema,
  advisories: z.array(z.string()).default([]),
  problems: z.array(z.unknown()).default([]),
});

// ── merge_derived.py ────────────────────────────────────────────────────────

const SEVERITIES = ['high', 'medium', 'low'] as const;
type Severity = (typeof SEVERITIES)[number];
type Counts = Record<Severity, number>;
const TAGS = ['agentic_ai', 'ai_automation'] as const;
type Tag = (typeof TAGS)[number];
const STRENGTH: Record<Tag, number> = { agentic_ai: 2, ai_automation: 1 };
const LABEL: Record<Tag, string> = { agentic_ai: 'Agentic AI', ai_automation: 'AI & Automation' };
export const SOLUTION_TYPES = [
  'process_fix',
  'new_system',
  'system_replacement',
  'integration',
  'automation',
  'ai_workflow',
  'agent',
] as const;

interface Step extends Obj {
  id: string;
  kind: string;
  actor: string;
  role?: string;
  why_human?: string;
  branches?: { next_step_id: string; condition: string }[];
}
interface Solution extends Obj {
  id: string;
  name: string;
  summary: string;
  type: string;
  delivery: string;
  steps: Step[];
  tag: Tag;
}
interface PainPoint extends Obj {
  id: string;
  name: string;
  severity: Severity;
  resolution: {
    has_solution: boolean;
    solution_id?: string;
    reason?: string;
    one_line_reason?: string;
  };
}
interface Priority extends Obj {
  level: Severity;
  reason: string;
  basis: Counts;
  has_solutions?: boolean;
  rank?: number;
}
interface Process extends Obj {
  id: string;
  name: string;
  pain_points: PainPoint[];
  bottleneck?: { id: string; name: string; reason: string; [k: string]: unknown } | null;
  priority: Priority;
  tag: string;
}
interface Department extends Obj {
  id: string;
  name: string;
  overview: { objectives: unknown };
  processes: Process[];
  priority: Priority;
}
interface Tool extends Obj {
  id: string;
  name: string;
  purpose: string;
  process_ids: string[];
  connected_to: string[];
}
interface Risk extends Obj {
  id: string;
  one_liner: string;
  department_id: string;
  solution_ids: string[];
}
interface Doc {
  business_profile: Obj & {
    team_size: unknown;
    strengths: (Obj & { text: string; department_id?: string })[];
    risks: Risk[];
    at_a_glance: Obj & { immediate_solution_ids: string[] };
    executive_diagnosis: Obj;
  };
  proposed_solutions: Obj & {
    solutions: Solution[];
    current_systems: Obj & { tools: Tool[]; grunt_work: (Obj & { process_id?: string })[] };
  };
  departments: Department[];
}

const zero = (): Counts => ({ high: 0, medium: 0, low: 0 });
const sum = (c: Counts) => c.high + c.medium + c.low;
const firstLevel = (c: Counts) => SEVERITIES.find((s) => c[s]);
const unique = <T>(xs: T[]) => [...new Set(xs)];
/** Python's `sorted(..., key=k, reverse=True)` on tuples: stable, largest first. */
const byKeyDesc = <T>(xs: T[], key: (x: T) => number[]) =>
  [...xs].sort((a, b) => {
    const ka = key(a);
    const kb = key(b);
    for (let i = 0; i < ka.length; i++) if (ka[i] !== kb[i]) return (kb[i] ?? 0) - (ka[i] ?? 0);
    return 0;
  });

const tagOf = (type: string): Tag => (type === 'agent' ? 'agentic_ai' : 'ai_automation');
const plural = (n: number, word: string) => `${n} ${word}${n !== 1 ? 's' : ''}`;

function counted(counts: Counts, noun: string): string {
  let parts = SEVERITIES.filter((s) => counts[s]).map((s) => `${counts[s]} ${s}`);
  if (parts.length > 1) parts = [`${parts.slice(0, -1).join(', ')} and ${parts.at(-1)}`];
  return parts[0] ? `${parts[0]}-${noun}` : '';
}

/** A step flows to the next in the list unless it declares branches. Terminals go nowhere. */
function edgesOf(sol: Solution) {
  const out: { from: string; to: string; condition: string | null }[] = [];
  sol.steps.forEach((s, i) => {
    if (s.kind === 'terminal') return;
    if (s.branches?.length) {
      for (const b of s.branches)
        out.push({ from: s.id, to: b.next_step_id, condition: b.condition });
    } else {
      const next = sol.steps[i + 1];
      if (next) out.push({ from: s.id, to: next.id, condition: null });
    }
  });
  return out;
}

function pickTag(votes: Partial<Record<Tag, Counts>>, heldBack: number): [string, string] {
  const tags = Object.keys(votes) as Tag[];
  if (tags.length === 0) {
    if (heldBack)
      return [
        'none',
        `${heldBack > 1 ? 'All ' : ''}${plural(heldBack, 'pain point')} ` +
          `${heldBack > 1 ? 'are' : 'is'} held back on purpose; no solution is proposed.`,
      ];
    return ['none', 'No pain points found; the process works as it is.'];
  }
  const v = (t: Tag) => votes[t] ?? zero();
  const [winner, ...rest] = byKeyDesc(tags, (t) => [
    ...SEVERITIES.map((s) => v(t)[s]),
    STRENGTH[t],
  ]);
  if (!winner) throw new Error('unreachable');
  const label = LABEL[winner];
  const other = rest[0];
  if (!other) {
    const n = sum(v(winner));
    const top = firstLevel(v(winner));
    return [
      winner,
      `${label} is the only solution type here (${plural(n, 'pain point')}, highest severity ${top}).`,
    ];
  }
  for (const s of SEVERITIES) {
    const a = v(winner)[s];
    const b = v(other)[s];
    if (a !== b)
      return [
        winner,
        `${label} covers more ${s}-severity pain points (${a} vs ${b} for ${LABEL[other]}).`,
      ];
  }
  return [winner, `Tied on severity with ${LABEL[other]}; ${label} is the stronger solution.`];
}

function processPriority(solved: Counts): Priority {
  const level = firstLevel(solved);
  if (!level)
    return {
      level: 'low',
      reason: 'No solution is proposed in this process.',
      basis: solved,
      has_solutions: false,
    };
  const n = sum(solved);
  return {
    level,
    reason: `Solutions address ${counted(solved, 'severity')} pain point${n !== 1 ? 's' : ''}.`,
    basis: solved,
    has_solutions: true,
  };
}

function departmentPriority(procs: Process[]): Priority {
  const basis = zero();
  for (const p of procs) if (p.priority.has_solutions) basis[p.priority.level] += 1;
  const level = firstLevel(basis);
  if (!level) return { level: 'low', reason: 'No solution is proposed in this department.', basis };
  const n = sum(basis);
  return {
    level,
    reason: `${counted(basis, 'priority')} process${n !== 1 ? 'es' : ''} with solutions.`,
    basis,
  };
}

const rankKey = (basis: Counts) => SEVERITIES.map((s) => basis[s]);

/** `merge_derived.merge`: every derived value, recomputed in place. */
function merge(doc: Doc): Doc {
  const bp = doc.business_profile;
  const ps = doc.proposed_solutions;
  const depts = doc.departments;
  const sols = new Map(ps.solutions.map((s) => [s.id, s]));
  const sol = (id: string | undefined) => {
    const s = sols.get(id ?? '');
    if (!s) throw new Error(`unknown solution ${id}`);
    return s;
  };
  const tools = ps.current_systems.tools;
  const procDept = new Map(depts.flatMap((d) => d.processes.map((p) => [p.id, d] as const)));
  const addressed = new Map<string, [Department, Process, PainPoint][]>(
    [...sols.keys()].map((id) => [id, []]),
  );
  const holdingBack: Obj[] = [];

  for (const s of ps.solutions) s.tag = tagOf(s.type);

  for (const d of depts) {
    d.processes.forEach((p, i) => {
      p.number = i + 1;
      const votes: Partial<Record<Tag, Counts>> = {};
      const solved = zero();
      for (const pp of p.pain_points) {
        const r = pp.resolution;
        if (r.has_solution) {
          const t = sol(r.solution_id).tag;
          pp.tag = t;
          (votes[t] ??= zero())[pp.severity] += 1;
          solved[pp.severity] += 1;
          addressed.get(r.solution_id ?? '')?.push([d, p, pp]);
        } else {
          pp.tag = 'none';
          holdingBack.push({
            pain_point_id: pp.id,
            name: pp.name,
            process_id: p.id,
            department_id: d.id,
            reason: r.reason,
            one_line_reason: r.one_line_reason,
          });
        }
      }
      const total = p.pain_points.length;
      const held = total - sum(solved);
      p.pain_point_count = total;
      p.solved_count = total - held;
      p.held_back_count = held;
      [p.tag, p.tag_reason] = pickTag(votes, held);
      p.tag_votes = votes;
      p.priority = processPriority(solved);
      p.tools = tools
        .filter((t) => t.process_ids.includes(p.id))
        .map((t) => ({ id: t.id, name: t.name }));
      if (p.bottleneck) {
        p.bottleneck.tag = p.tag;
        p.bottleneck.impact = { level: p.priority.level, reason: p.priority.reason };
      }
    });

    d.priority = departmentPriority(d.processes);
    d.strengths = bp.strengths.filter((s) => s.department_id === d.id).map((s) => s.text);
    d.bottlenecks = d.processes
      .filter((p) => p.bottleneck)
      .map((p) => ({ id: p.bottleneck?.id, name: p.bottleneck?.name, process_id: p.id }));
    d.risks = bp.risks
      .filter((r) => r.department_id === d.id)
      .map((r) => ({ id: r.id, one_liner: r.one_liner }));
    d.process_count = d.processes.length;
    d.tag_counts = Object.fromEntries(
      (['agentic_ai', 'ai_automation', 'none'] as const).map((t) => [
        t,
        d.processes.filter((p) => p.tag === t).length,
      ]),
    );
    const used = new Set(
      d.processes.flatMap((p) =>
        p.pain_points
          .filter((pp) => pp.resolution.has_solution)
          .map((pp) => pp.resolution.solution_id),
      ),
    );
    d.solution_counts = Object.fromEntries(
      TAGS.map((t) => [t, [...used].filter((s) => sol(s).tag === t).length]),
    );
  }

  byKeyDesc(depts, (d) => rankKey(d.priority.basis)).forEach((d, i) => {
    d.priority.rank = i + 1;
  });

  for (const [sid, s] of sols) {
    const hits = addressed.get(sid) ?? [];
    s.addresses = {
      pain_point_ids: hits.map(([, , pp]) => pp.id),
      process_ids: unique(hits.map(([, p]) => p.id)),
      department_ids: unique(hits.map(([d]) => d.id)),
    };
    const sev = zero();
    for (const [, , pp] of hits) sev[pp.severity] += 1;
    const level = firstLevel(sev);
    if (!level) throw new Error(`solution ${sid} resolves no pain point`);
    const top = hits.find(([, , pp]) => pp.severity === level)?.[2].name;
    s.impact = {
      level,
      reason:
        `Resolves ${counted(sev, 'severity')} pain point${hits.length !== 1 ? 's' : ''}; ` +
        `the most severe is "${top}".`,
    };
    s.edges = edgesOf(s);
    s.human_involvement = s.steps
      .filter((st) => st.actor !== 'system')
      .map((st) => ({ step_id: st.id, role: st.role, why: st.why_human }));
    s.step_count = s.steps.length;
  }

  const links = new Map(tools.map((t) => [t.id, new Set(t.connected_to)]));
  for (const t of tools) {
    const others = [...links].filter(([, c]) => c.has(t.id)).map(([o]) => o);
    t.connected_with = [...new Set([...(links.get(t.id) ?? []), ...others])].sort();
    t.connected = (t.connected_with as string[]).length > 0;
    t.department_ids = unique(t.process_ids.map((p) => procDept.get(p)?.id));
  }

  const all = [...sols.values()];
  const count = (f: (s: Solution) => boolean) => all.filter(f).length;
  ps.holding_back = holdingBack;
  const totals = {
    solution_count: sols.size,
    by_tag: Object.fromEntries(TAGS.map((t) => [t, count((s) => s.tag === t)])),
    by_type: Object.fromEntries(
      SOLUTION_TYPES.map((t) => [t, count((s) => s.type === t)] as const).filter(([, n]) => n),
    ),
    by_delivery: Object.fromEntries(
      ['in_house', 'adapted_in_house', 'external'].map((k) => [k, count((s) => s.delivery === k)]),
    ),
  };
  ps.totals = totals;

  const allProcs = depts.flatMap((d) => d.processes.map((p) => [d, p] as const));
  bp.key_findings = {
    team_size: bp.team_size,
    department_count: depts.length,
    departments: depts.map((d) => ({ id: d.id, name: d.name, objectives: d.overview.objectives })),
    processes_by_department: depts.map((d) => ({
      department_id: d.id,
      department: d.name,
      processes: d.processes.map((p) => p.name),
    })),
    process_count: allProcs.length,
    tools_in_use: tools.map((t) => ({ id: t.id, name: t.name, purpose: t.purpose })),
    risks_vs_solutions: bp.risks.map((r) => ({
      risk_id: r.id,
      risk: r.one_liner,
      solutions: r.solution_ids.map((s) => ({ id: s, name: sol(s).name, summary: sol(s).summary })),
    })),
  };
  bp.bottlenecks_table = allProcs
    .filter(([, p]) => p.bottleneck)
    .map(([d, p]) => ({
      bottleneck_id: p.bottleneck?.id,
      name: p.bottleneck?.name,
      where: { department_id: d.id, process_id: p.id },
      cause: p.bottleneck?.reason,
      impact_level: (p.bottleneck?.impact as { level: string } | undefined)?.level,
    }));
  bp.priority_matrix = byKeyDesc(allProcs, ([, p]) => rankKey(p.priority.basis)).map(([d, p]) => ({
    process_id: p.id,
    process: p.name,
    department_id: d.id,
    department: d.name,
    level: p.priority.level,
    reason: p.priority.reason,
  }));
  bp.at_a_glance.opportunities = {
    solution_count: totals.solution_count,
    by_tag: totals.by_tag,
    immediate: bp.at_a_glance.immediate_solution_ids.map((s) => ({ id: s, name: sol(s).name })),
  };
  return doc;
}

// ── merge_lint_edit.py ──────────────────────────────────────────────────────

const ID = /^[a-z0-9]+(-[a-z0-9]+)*$/;
const ENUMS = new Set([
  '2', 'high', 'medium', 'low', 'current_is_best', 'would_break', 'agentic_ai',
  'ai_automation', 'none', 'in_house', 'adapted_in_house', 'external', 'retyped',
  'forwarded', 'client_quote', 'client_data', 'steps_removed', 'in_house_running',
  'trigger', 'action', 'ai_agent', 'decision', 'human_in_loop', 'terminal',
  'system', 'human', 'hybrid', ...SOLUTION_TYPES,
]); // prettier-ignore

/** Prose: every string that is neither an id nor a contract enum value. Only prose may be edited. */
export function isProse(v: unknown): v is string {
  return typeof v === 'string' && !ID.test(v) && !ENUMS.has(v);
}

/** `_shape`: the document with every prose string replaced by one placeholder. */
function shape(node: unknown): unknown {
  if (Array.isArray(node)) return node.map(shape);
  if (node && typeof node === 'object')
    return Object.fromEntries(Object.entries(node).map(([k, v]) => [k, shape(v)]));
  return isProse(node) ? '…' : node;
}

/** Python `==` on JSON values: key order does not matter. */
function deepEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (Array.isArray(a) || Array.isArray(b))
    return (
      Array.isArray(a) &&
      Array.isArray(b) &&
      a.length === b.length &&
      a.every((x, i) => deepEqual(x, b[i]))
    );
  if (a && b && typeof a === 'object' && typeof b === 'object') {
    const ka = Object.keys(a);
    if (ka.length !== Object.keys(b).length) return false;
    return ka.every((k) => k in b && deepEqual((a as Obj)[k], (b as Obj)[k]));
  }
  return false;
}

/** Departments the executive diagnosis names: they can never be removed. */
export function diagnosedDepartments(doc: AtlasDocument): Set<string> {
  return new Set(
    Object.values(doc.business_profile.executive_diagnosis).flatMap((v) =>
      v && typeof v === 'object' && typeof (v as Obj).department_id === 'string'
        ? [(v as Obj).department_id as string]
        : [],
    ),
  );
}

/**
 * `without_departments`: the document less the given departments and what they
 * own (their solutions, risks, strengths, grunt work and tool uses), with every
 * derived value recomputed.
 */
export function removeDepartments<T extends AtlasDocument>(doc: T, ids: Iterable<string>): T {
  const gone = new Set(ids);
  if (gone.size === 0) return doc;
  const out = structuredClone(doc) as unknown as Doc;
  const bp = out.business_profile;
  const ps = out.proposed_solutions;
  const removed = out.departments.filter((d) => gone.has(d.id));
  const procs = new Set(removed.flatMap((d) => d.processes.map((p) => p.id)));
  const sols = new Set(
    removed.flatMap((d) =>
      d.processes.flatMap((p) =>
        p.pain_points
          .filter((pp) => pp.resolution.has_solution)
          .map((pp) => pp.resolution.solution_id),
      ),
    ),
  );

  out.departments = out.departments.filter((d) => !gone.has(d.id));
  ps.solutions = ps.solutions.filter((s) => !sols.has(s.id));
  const cs = ps.current_systems;
  for (const t of cs.tools) t.process_ids = t.process_ids.filter((p) => !procs.has(p));
  cs.grunt_work = cs.grunt_work.filter((g) => !procs.has(g.process_id ?? ''));
  bp.strengths = bp.strengths.filter((s) => !gone.has(s.department_id ?? ''));
  bp.risks = bp.risks.filter((r) => !gone.has(r.department_id));
  for (const r of bp.risks) r.solution_ids = r.solution_ids.filter((s) => !sols.has(s));
  bp.at_a_glance.immediate_solution_ids = bp.at_a_glance.immediate_solution_ids.filter(
    (s) => !sols.has(s),
  );
  return merge(out) as unknown as T;
}

/**
 * `check`: why `edited` may not replace the board; empty when it may. Checked
 * against `original` (what Finalize produced), so any version is acceptable,
 * including one that puts a removed department back.
 */
export function checkEdit(original: AtlasDocument, edited: unknown): string[] {
  if (!edited || typeof edited !== 'object' || Array.isArray(edited))
    return ['the document must be a JSON object'];
  const depts = (edited as Obj).departments;
  if (
    !Array.isArray(depts) ||
    !depts.every((d) => d && typeof d === 'object' && 'id' in (d as Obj))
  )
    return ['departments must be a list of departments with ids'];
  const kept = new Set(depts.map((d) => (d as Obj).id as string));
  const known = new Set(original.departments.map((d) => d.id));
  const added = [...kept].filter((k) => !known.has(k)).sort();
  if (added.length) return [`departments cannot be added: ${added.join(', ')}`];
  const removed = [...known].filter((k) => !kept.has(k));
  const diagnosed = diagnosedDepartments(original);
  const blocked = removed.filter((d) => diagnosed.has(d)).sort();
  if (blocked.length)
    return [
      `departments named in the executive diagnosis cannot be removed: ${blocked.join(', ')}`,
    ];
  const before = shape(removeDepartments(original, removed)) as Obj;
  const after = shape(edited) as Obj;
  return [...new Set([...Object.keys(before), ...Object.keys(after)])]
    .sort()
    .filter((k) => !deepEqual(before[k], after[k]))
    .map((k) => `${k} cannot be changed by an edit`);
}

// ── prose edits, by stable path ─────────────────────────────────────────────

/*
 * The editor keeps staff's prose edits by a path that survives removing and
 * restoring departments: array items are named by their id (or another id
 * field, with a count when it repeats), not by position.
 */

const KEYS = [
  'id',
  'pain_point_id',
  'bottleneck_id',
  'risk_id',
  'step_id',
  'process_id',
  'department_id',
];

function itemKeys(items: unknown[]): string[] {
  const seen = new Map<string, number>();
  return items.map((item, i) => {
    const o = item && typeof item === 'object' ? (item as Obj) : null;
    const k = o && KEYS.find((key) => typeof o[key] === 'string');
    if (!o || !k) return String(i);
    const base = `${k}=${String(o[k])}`;
    const n = seen.get(base) ?? 0;
    seen.set(base, n + 1);
    return n ? `${base}#${n}` : base;
  });
}

/** Every prose string in the document, by stable path (`a/b/[id=x]/c`). */
export function proseFields(doc: unknown): Map<string, string> {
  const out = new Map<string, string>();
  const walk = (node: unknown, path: string) => {
    if (Array.isArray(node)) {
      const keys = itemKeys(node);
      node.forEach((x, i) => walk(x, `${path}/[${keys[i]}]`));
    } else if (node && typeof node === 'object') {
      for (const [k, v] of Object.entries(node)) walk(v, path ? `${path}/${k}` : k);
    } else if (isProse(node)) out.set(path, node);
  };
  walk(doc, '');
  return out;
}

/** Splits `a/b/[id=x]/c` into segments, keeping `/` inside a bracketed key. */
function segments(path: string): string[] {
  return path.match(/\[[^\]]*\]|[^/]+/g) ?? [];
}

/** Writes prose edits into a copy of the document; paths that no longer exist are skipped. */
export function applyProse<T>(doc: T, edits: ReadonlyMap<string, string>): T {
  if (edits.size === 0) return doc;
  const out = structuredClone(doc);
  for (const [path, value] of edits) {
    const segs = segments(path);
    let node: unknown = out;
    for (const [i, seg] of segs.entries()) {
      const last = i === segs.length - 1;
      if (Array.isArray(node) && seg.startsWith('[')) {
        const idx = itemKeys(node).indexOf(seg.slice(1, -1));
        if (idx < 0) break;
        if (last) {
          if (isProse(node[idx])) node[idx] = value;
        } else node = node[idx];
      } else if (node && typeof node === 'object' && !Array.isArray(node)) {
        const o = node as Obj;
        if (!(seg in o)) break;
        if (last) {
          if (isProse(o[seg])) o[seg] = value;
        } else node = o[seg];
      } else break;
    }
  }
  return out;
}

/**
 * The document staff are building: the original less the removed departments,
 * with their prose edits. Edits go in before the derivation (so copied names
 * follow an edited one) and again after (so an edit to a derived sentence stays).
 */
export function buildEdited(
  original: AtlasDocument,
  removed: Iterable<string>,
  edits: ReadonlyMap<string, string>,
): AtlasDocument {
  return applyProse(removeDepartments(applyProse(original, edits), removed), edits);
}

/** The removed departments and prose edits that turn `original` into `version`. */
export function editsOf(
  original: AtlasDocument,
  version: AtlasDocument,
): { removed: Set<string>; edits: Map<string, string> } {
  const kept = new Set(version.departments.map((d) => d.id));
  const removed = new Set(original.departments.map((d) => d.id).filter((id) => !kept.has(id)));
  const base = proseFields(removeDepartments(original, removed));
  const edits = new Map<string, string>();
  for (const [path, v] of proseFields(version)) if (base.get(path) !== v) edits.set(path, v);
  return { removed, edits };
}

export interface FieldChange {
  path: string;
  before: string;
  after: string;
}

/** What changed from `a` to `b`: prose by path, and each department removed or restored as one change. */
export function diffEdits(a: AtlasDocument, b: AtlasDocument): FieldChange[] {
  const ids = (d: AtlasDocument) => new Set(d.departments.map((x) => x.id));
  const [ia, ib] = [ids(a), ids(b)];
  const name = (d: AtlasDocument, id: string) => d.departments.find((x) => x.id === id)?.name ?? id;
  const changes: FieldChange[] = [
    ...[...ia]
      .filter((id) => !ib.has(id))
      .map((id) => ({ path: `departments/[id=${id}]`, before: name(a, id), after: '(removed)' })),
    ...[...ib]
      .filter((id) => !ia.has(id))
      .map((id) => ({ path: `departments/[id=${id}]`, before: '(removed)', after: name(b, id) })),
  ];
  const pa = proseFields(a);
  for (const [path, after] of proseFields(b)) {
    const before = pa.get(path);
    if (before !== undefined && before !== after) changes.push({ path, before, after });
  }
  return changes;
}

// ── API shapes ──────────────────────────────────────────────────────────────

export interface MergeLintVersionInfo {
  version: number;
  createdAt: string;
  createdBy: { id: string; name: string } | null;
  note: string | null;
  pushedToAtlasAt: string | null;
}

export interface MergeLintResponse {
  /** False until Atlas has produced the document for the run. */
  ready: boolean;
  version: MergeLintVersionInfo | null;
  content: AtlasDocument | null;
  /** What Finalize produced (version 1): edits are built and checked against it. */
  original: AtlasDocument | null;
  /** Atlas's findings while merging (pain points left out, quotes dropped). */
  findings: string[];
  versions: MergeLintVersionInfo[];
  latestVersion: number | null;
  /** Edits are locked once the final review is marked done. */
  locked: boolean;
}

export const SaveMergeLintSchema = z.object({
  baseVersion: z.number().int().positive(),
  content: z.unknown(),
  note: z.string().trim().max(1000).optional(),
});
